import React, { useState } from 'react'
import { View, useWindowDimensions, ScrollView, Pressable, ActivityIndicator, TouchableOpacity, StyleSheet } from 'react-native'
import { FormikErrors, FormikTouched } from 'formik'
import Popover from 'react-native-popover-view/dist/Popover'
import { ZenButton, ZenCheckBox, ZenDropdown, ZenIcon, ZenInput, ZenRadioButton, ZenSwitch, ZenText, ZenToast, ZenToolTip } from 'zen-core-elements'
import { zonedropdownVal, TempItem } from './taxModule'
import { fontSize, appColors, appFonts } from '../../themes/appTheme'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'

interface TaxDetailsProps {
    formik: any;
    zoneVal: any[];
    isView: boolean;
    duplicateLoader: boolean;
    duplicateValue: any;
    setNameExist: (name: string) => void;
    taxId: number | null | undefined;
    navigation: any;
}

const TaxDetails: React.FC<TaxDetailsProps> = ({
    formik,
    zoneVal,
    isView,
    duplicateLoader,
    duplicateValue,
    setNameExist,
    taxId,
    navigation,
}) => {
    const { width, height } = useWindowDimensions()
    const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
    const [selectedZoneIndex, setSelectedZoneIndex] = useState<number | null>(null)

    const mediumValue = [{ label: "POS", value: 'POS' }, { label: "WEB", value: 'WEB' }]

    // State variable used to delete the zone dialogbox props
    const deleteDialogProps = {
        content: 'Are you sure you want to remove this zone?',
        button: [{ id: 1, name: 'No', color: appColors.primary, backgroundColor: appColors.light }, { id: 2, name: 'Yes', color: appColors.light, backgroundColor: appColors.primary }],
        icon: { name: 'delete-outline', color: appColors.inActiveColor, type: 'material-community' }
    }

    // Method which is used to add zones
    const addZones = () => {
        let isError = [...(formik.errors.zones ? formik.errors?.zones : [])]
        isError = isError.filter((i: any) => i !== undefined)
        if (isError.length > 0) {
            ZenToast({ type: 'info', message: 'missing require fields' })
        } else {
            let addNewZone = [...formik?.values?.zones]
            let zoneField: TempItem = { rowId: null, label: '', value: '', dropdown: false, zone: { id: 0, medium: '', zoneName: '' } as zonedropdownVal, zoneEnable: true }
            addNewZone.push(zoneField)
            formik.setFieldValue('zones', addNewZone)
        }
    }

    const removeZone = (index: number) => {
        let zoneArr = [...formik.values.zones]
        zoneArr = zoneArr.filter((_, zoneIndex) => zoneIndex !== index) || []
        formik.setFieldValue('zones', [...zoneArr])
        if (index !== formik.values.zones.length - 1) {
            formik.setFieldTouched(`zones[${formik.values.zones.length - 1}]`, false)
        }
        formik.setFieldTouched(`zones[${index}]`, false)
    }

    const RemoveConfirmation = (value: { name: string }) => {
        if (value?.name === 'Yes' && deleteConfirmation) {
            setDeleteConfirmation(false)
            removeZone(selectedZoneIndex ?? 0)
        } else {
            setDeleteConfirmation(false)
        }
    }

    const isLandscape = width > height;

    return (
        <ScrollView showsVerticalScrollIndicator={false} style={styles.container}>
            <View style={styles.content}>

                {/* Basic Information Section */}
                <View style={styles.card}>
                    <View style={styles.cardHeader}>
                        <View>
                            <ZenText content='Basic Information' bold={true} size='medium' color={appColors.primary} style={{ fontFamily: appFonts.bold }} />
                            <ZenText content='Set manual tax name, description and general status.' color={appColors.labelColor} style={{ fontSize: fontSize.smallerFontSize, marginTop: 2 }} />
                        </View>
                    </View>

                    <View style={[styles.row, { flexDirection: isLandscape ? 'row' : 'column' }]}>
                        <View style={{ flex: 1 }}>
                            <ZenInput
                                label='Manual tax name *'
                                labelStyle={styles.inputLabel}
                                onChangeText={(text: string) => { formik?.setFieldValue('taxName', text); setNameExist(text.trim()); }}
                                value={formik?.values?.taxName}
                                disabled={isView}
                                inputContainerStyle={styles.inputContainer}
                                inputStyle={styles.inputText}
                                rightIcon={duplicateLoader ? <ActivityIndicator size='small' color={appColors.secondary} /> : undefined}
                                onBlur={() => { formik?.setFieldTouched('taxName', true); formik?.setFieldValue('taxName', formik?.values?.taxName?.trim()) }}
                                errorMessage={formik?.touched?.taxName && formik?.errors?.taxName ? formik?.errors?.taxName : duplicateValue?.found ? "Manual tax name already exists!" : undefined}
                                errorStyle={styles.errorText}
                            />
                        </View>

                        <View style={{ flex: 1 }}>
                            <ZenInput
                                label='Tax for'
                                labelStyle={styles.inputLabel}
                                value='Specific Zone'
                                disabled={true}
                                editable={false}
                                inputContainerStyle={styles.inputContainerDisabled}
                                inputStyle={styles.inputTextDisabled}
                            />
                        </View>
                    </View>

                    <View style={{ marginTop: 10 }}>
                        <ZenInput
                            label='Description'
                            labelStyle={styles.inputLabel}
                            multiline={true}
                            disabled={isView}
                            value={formik?.values?.description}
                            inputContainerStyle={[styles.inputContainer, { height: 75, alignItems: 'flex-start', paddingTop: 6 }]}
                            inputStyle={[styles.inputText, { textAlignVertical: 'top' }]}
                            onChangeText={(text: string) => formik?.setFieldValue('description', text)}
                            errorMessage={formik?.touched?.description && formik?.errors?.description ? formik?.errors?.description || '' : ''}
                            errorStyle={styles.errorText}
                            onBlur={formik?.handleBlur('description')}
                        />
                    </View>

                    {/* Status & Options Row */}
                    <View style={[styles.optionsRow, { flexDirection: isLandscape ? 'row' : 'column', gap: 15 }]}>
                        <View style={[styles.optionItem, { gap: 10 }]}>
                            <ZenCheckBox
                                checkedColor={appColors.primary}
                                disabled={isView}
                                onPress={() => { formik.setFieldValue('isEnabledAllProducts', !formik?.values?.isEnabledAllProducts) }}
                                checked={formik?.values?.isEnabledAllProducts}
                                checkedIcon='check-square-o'
                                unCheckedIcon='square-o'
                                titleStyle={{ fontSize: fontSize.smallFontSize, fontFamily: appFonts.medium, color: appColors.lightDark }}
                                title='Apply tax to all taxable products'
                            />
                            <ZenToolTip
                                content={
                                    <View style={{ padding: 10, backgroundColor: appColors.lightDark, borderRadius: 6 }}>
                                        <ZenText content='This tax is applied to all the taxable products in your store' color={appColors.light} size={fontSize.errorFontSize} />
                                    </View>}
                                height={60}
                                width={240}
                                popoverStyle={{ elevation: 2 }}
                                backgroundColor="transparent"
                                popover={
                                    <Pressable style={{ marginLeft: -4 }}>
                                        <ZenIcon name='exclamationcircle' type='antdesign' size={16} color={appColors.grey} />
                                    </Pressable>
                                }
                            />
                        </View>

                        <View style={[styles.optionItem, { alignSelf: isLandscape ? 'center' : 'flex-start' }]}>
                            <ZenSwitch
                                activeColor={appColors?.primary}
                                inActiveColor={appColors?.borderColor}
                                activeThumbColor={appColors?.light}
                                inactiveThumbColor={appColors?.light}
                                style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                                value={formik?.values?.isEnabled || false}
                                disabled={isView}
                                onValueChange={(val) => { formik?.setFieldValue('isEnabled', val) }}
                            />
                            <ZenText
                                content={formik?.values?.isEnabled ? 'Enabled' : "Disabled"}
                                color={formik?.values?.isEnabled ? appColors.primary : appColors.labelColor}
                                size={'small'}
                                bold={true}
                                style={{ marginLeft: 6, fontFamily: appFonts.bold }}
                            />
                        </View>

                    </View>

                    <View style={styles.actionBtnContainer}>
                        <ZenButton
                            title='Manage Store Zones'
                            type='outline'
                            outlineColor={appColors.primary}
                            disabled={isView}
                            onPress={() => { navigation.navigate('ZoneStack') }}
                            titleStyle={{ color: appColors.primary, fontFamily: appFonts.bold, fontSize: fontSize.smallerFontSize }}
                            buttonStyle={styles.outlineBtn}
                        />
                    </View>
                </View>

                {/* Zones and Percentage Settings Section */}
                <View style={styles.card}>
                    <View style={[styles.cardHeader, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
                        <View style={{ flex: 1 }}>
                            <ZenText content='Zones & Percentages' bold={true} size='medium' color={appColors.primary} style={{ fontFamily: appFonts.bold }} />
                            <ZenText content='Map tax rates for specific store geographical zones.' color={appColors.labelColor} style={{ fontSize: fontSize.smallerFontSize, marginTop: 2 }} />
                        </View>
                        <ZenButton
                            title='Add Zone'
                            type='solid'
                            disabled={isView}
                            onPress={() => { addZones() }}
                            titleStyle={{ color: appColors.light, fontFamily: appFonts.bold, fontSize: fontSize.smallerFontSize }}
                            buttonStyle={[styles.solidBtn, { minWidth: 100 }]}
                        />
                    </View>

                    <View style={{ marginTop: 15 }}>
                        {
                            Array.isArray(formik.values.zones) && formik.values.zones.map((itemVal, index) => (
                                <View key={index} style={styles.zoneCard}>
                                    <View style={styles.zoneCardHeader}>
                                        <ZenText content={`Zone Configuration #${index + 1}`} bold={true} style={{ fontFamily: appFonts.bold, fontSize: fontSize.smallFontSize, color: appColors.lightDark }} />
                                        {!isView && formik?.values?.zones?.length > 1 && (
                                            <TouchableOpacity
                                                style={styles.deleteIconContainer}
                                                onPress={() => {
                                                    if (formik?.values?.zones?.length > 1) {
                                                        let isFieldTouched = formik?.touched?.zones && formik?.touched?.zones[index] && formik?.touched?.zones[index] || false
                                                        if (isFieldTouched) {
                                                            setDeleteConfirmation(true)
                                                            setSelectedZoneIndex(index)
                                                        } else {
                                                            removeZone(index)
                                                        }
                                                    } else {
                                                        ZenToast({ type: 'info', message: 'At least one zone is required' })
                                                    }
                                                }}
                                            >
                                                <ZenIcon name='delete-outline' type='material-community' size={20} color={appColors.red} />
                                            </TouchableOpacity>
                                        )}
                                    </View>

                                    <View style={[styles.row, { flexDirection: isLandscape ? 'row' : 'column', marginTop: 10, gap: 12 }]}>
                                        <View style={{ flex: 1.5 }}>
                                            <ZenDropdown
                                                title='Zone*'
                                                multiple={false}
                                                items={zoneVal}
                                                value={itemVal?.zone?.id || 0}
                                                setValue={() => { }}
                                                open={itemVal?.dropdown}
                                                setOpen={() => { }}
                                                disabled={isView}
                                                onPress={() => {
                                                    if (!isView) {
                                                        formik.setFieldValue(`zones[${index}].dropdown`, !itemVal?.dropdown)
                                                        formik.setFieldTouched(`zones[${index}].dropdown`, true)
                                                    }
                                                }}
                                                onSelectItem={(val) => {
                                                    formik.setFieldValue(`zones[${index}].dropdown`, false)
                                                    formik.setFieldValue(`zones[${index}].zone`, val.key)
                                                }}
                                                maxHeight={150}
                                                searchable={true}
                                                searchPlaceholder="Search Zone"
                                                listItemLabelStyle={{ fontSize: fontSize.smallerFontSize }}
                                                titleStyle={styles.inputLabel}
                                                labelStyle={{ color: appColors.lightDark, fontSize: fontSize.smallerFontSize, fontFamily: appFonts?.medium }}
                                                placeholder="Select Zone"
                                                style={styles.dropdownInput}
                                                searchPlaceholderTextColor={appColors.grey}
                                                mainContainerStyle={{ width: '100%' }}
                                                ListEmptyComponent={() => (
                                                    <View style={{ alignItems: "center", paddingVertical: 10 }}>
                                                        <ZenButton title="No Zones found" onPress={() => { navigation.navigate("ZoneStack") }} buttonStyle={{ backgroundColor: appColors.light, borderRadius: 7, borderWidth: 1, borderColor: appColors.primary, paddingHorizontal: 10 }} titleStyle={{ color: appColors.primary }} />
                                                    </View>
                                                )}
                                            />
                                            <View style={{ height: 16 }}>
                                                <ZenText content={(formik?.errors?.zones && formik.touched.zones) && (formik?.errors?.zones[index] && (formik.touched.zones[index] as FormikTouched<TempItem>)?.dropdown) ? ((formik.errors.zones[index] as FormikErrors<TempItem>)?.zone as FormikErrors<zonedropdownVal>)?.zoneName || "" : ""} size={fontSize.errorFontSize} style={styles.errorText} />
                                            </View>
                                        </View>

                                        <View style={{ flex: 1 }}>
                                            <ZenInput
                                                label='Tax Percentage *'
                                                labelStyle={styles.inputLabel}
                                                value={itemVal?.zoneRate ? String(itemVal?.zoneRate) : ''}
                                                disabled={isView}
                                                containerStyle={{ width: '100%' }}
                                                inputContainerStyle={styles.inputContainer}
                                                inputStyle={styles.inputText}
                                                keyboardType='numeric'
                                                onBlur={() => { formik.setFieldTouched(`zones[${index}].zoneRate`, true) }}
                                                onChangeText={(val: string) => {
                                                    // Keep what was typed: parseFloat turned "12abc" into 12 and "7." into 7 mid-typing.
                                                    // The schema validates it; addEditTax converts to a number on save.
                                                    formik.setFieldValue(`zones[${index}].zoneRate`, val)
                                                }}
                                                errorMessage={(formik.errors.zones && formik.touched.zones) ? ((formik.errors.zones[index] as FormikErrors<TempItem>)?.zoneRate && (formik?.touched?.zones[index] as FormikTouched<TempItem>)?.zoneRate) ? (formik.errors.zones[index] as FormikErrors<TempItem>)?.zoneRate : '' : ''}
                                                errorStyle={styles.errorText}
                                            />
                                        </View>
                                    </View>

                                    <View style={styles.zoneSwitchRow}>
                                        <ZenText content="Zone Tax Mapping Status:" color={appColors.labelColor} style={{ fontSize: fontSize.smallerFontSize }} />
                                        <Pressable style={{ flexDirection: "row", alignItems: "center" }} disabled={isView} onPress={() => formik.setFieldValue(`zones[${index}].zoneEnable`, !itemVal.zoneEnable)}>
                                            <ZenSwitch
                                                value={itemVal.zoneEnable || false}
                                                activeColor={appColors?.primary}
                                                inActiveColor={appColors?.borderColor}
                                                activeThumbColor={appColors?.light}
                                                inactiveThumbColor={appColors?.light}
                                                disabled={isView}
                                                style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                                                onValueChange={() => {
                                                    formik.setFieldValue(`zones[${index}].zoneEnable`, !itemVal.zoneEnable)
                                                }}
                                            />
                                            <ZenText content={itemVal.zoneEnable ? "Active" : "Disabled"} color={itemVal.zoneEnable ? appColors.primary : appColors.labelColor} style={{ fontSize: fontSize.smallerFontSize, fontFamily: appFonts.bold, marginLeft: 6 }} />
                                        </Pressable>
                                    </View>
                                </View>
                            ))
                        }
                    </View>

                    {/* {taxId && (
                        <View style={styles.mediumContainer}>
                            <ZenText content='Platform Medium' style={[styles.inputLabel, { marginBottom: 8 }]} />
                            <ZenRadioButton
                                options={mediumValue}
                                initialSelected={formik?.values?.medium}
                                containerStyle={{ flexDirection: "row", columnGap: 20 }}
                                size={18}
                                disabled={isView}
                                onSelect={(item) => {
                                    formik?.setFieldValue('medium', item)
                                }}
                            />
                        </View>
                    )} */}

                    <View style={{ marginTop: 20 }}>
                        <ZenInput
                            label='Tax percentage for other areas'
                            labelStyle={styles.inputLabel}
                            onChangeText={(val: string) => { formik?.setFieldValue('otherZoneRate', parseFloat(val) || '') }}
                            onBlur={() => { formik?.setFieldTouched("otherZoneRate", true) }}
                            value={String(formik?.values?.otherZoneRate)}
                            disabled={isView}
                            inputStyle={styles.inputText}
                            inputContainerStyle={styles.inputContainer}
                            errorMessage={(formik?.touched?.otherZoneRate && formik?.errors?.otherZoneRate) ? (formik?.touched?.otherZoneRate && formik?.errors?.otherZoneRate) : null}
                            errorStyle={styles.errorText}
                            keyboardType='numeric'
                            rightIcon={
                                <ZenToolTip
                                    content={<ZenText content='This tax percentage will be applied to all other areas not configured above' color={appColors.light} size={fontSize.errorFontSize} />}
                                    height={60}
                                    width={260}
                                    backgroundColor={appColors?.lightDark}
                                    popoverStyle={{ elevation: 2 }}
                                    popover={<Pressable style={{ padding: 4 }}>
                                        <ZenIcon type='antdesign' name="exclamationcircle" size={15} color={appColors.grey} />
                                    </Pressable>}
                                />
                            }
                        />
                    </View>
                </View>
            </View>

            <Popover isVisible={deleteConfirmation} popoverStyle={{ width: width > 900 ? width / 2 : width > 600 ? 450 : 350, borderRadius: 10 }}>
                <CommonDialogBox type="OTHER" dialog={deleteDialogProps} confirmation={RemoveConfirmation} />
            </Popover>
        </ScrollView>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f8f9fa',
    },
    content: {
        gap: 16,
        padding: 12,
        paddingBottom: 40,
    },
    card: {
        borderRadius: 12,
        backgroundColor: appColors.light,
        padding: 16,
        borderWidth: 1,
        borderColor: '#eef0f2',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 3,
        elevation: 1,
    },
    cardHeader: {
        marginBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#f1f3f5',
        paddingBottom: 12,
    },
    row: {
        gap: 12,
    },
    inputLabel: {
        fontSize: fontSize.smallFontSize,
        color: appColors.labelColor,
        fontFamily: appFonts.medium,
        marginBottom: 6,
        fontWeight: '500',
    },
    inputContainer: {
        borderColor: '#dcdfe6',
        borderWidth: 1,
        borderRadius: 8,
        height: 48,
        backgroundColor: '#ffffff',
        paddingHorizontal: 12,
    },
    inputContainerDisabled: {
        borderColor: '#e4e7ed',
        borderWidth: 1,
        borderRadius: 8,
        height: 48,
        backgroundColor: '#f5f7fa',
        paddingHorizontal: 12,
    },
    inputText: {
        color: appColors.lightDark,
        fontFamily: appFonts.medium,
        fontSize: fontSize.mediumFontSize,
    },
    inputTextDisabled: {
        color: '#909399',
        fontFamily: appFonts.medium,
        fontSize: fontSize.mediumFontSize,
    },
    errorText: {
        color: appColors.red,
        fontSize: fontSize.errorFontSize,
        marginTop: 2,
    },
    optionsRow: {
        marginTop: 10,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: '#f1f3f5',
        paddingTop: 16,
    },
    optionItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    actionBtnContainer: {
        alignItems: 'flex-start',
        marginTop: 12,
    },
    outlineBtn: {
        borderRadius: 8,
        borderColor: appColors.primary,
        borderWidth: 1.5,
        paddingHorizontal: 16
    },
    solidBtn: {
        borderRadius: 8,
        backgroundColor: appColors.primary,
        paddingHorizontal: 16,
    },
    zoneCard: {
        backgroundColor: '#fafbfc',
        borderRadius: 10,
        padding: 16,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: '#e8ecef',
    },
    zoneCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#eaeff3',
        paddingBottom: 8,
        marginBottom: 4,
    },
    deleteIconContainer: {
        padding: 4,
        backgroundColor: '#fff',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#fadcd9',
    },
    dropdownInput: {
        borderColor: '#dcdfe6',
        borderWidth: 1,
        borderRadius: 8,
        height: 48,
        backgroundColor: '#ffffff',
    },
    zoneSwitchRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: '#eaeff3',
    },
    mediumContainer: {
        marginTop: 12,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: '#f1f3f5',
    }
})

export default TaxDetails
